//
// Horizontal segmented bar chart — ported from takso-poc registry.jsx.
// Each part occupies a proportional slice; narrow segments show number only,
// wider segments show "N Label". A fixed-position tooltip appears on hover
// when a segment is too narrow to show its label.
//
// Props:
//   parts  — [{ label: string, n: number }]  (zero-count parts are filtered out)
//   total  — optional override; defaults to sum of parts

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { colorFor, textOn } from '../theme/palette';
import './charts.scss';

const n = (x) => (x == null ? 0 : Number(x)) || 0;

export function StackBar({ parts = [], total = 0 }) {
  const { t } = useTranslation();
  const [tip, setTip] = useState(null);

  const filled = parts.filter((p) => n(p.n) > 0);
  const safeTotal = total > 0 ? total : filled.reduce((a, p) => a + n(p.n), 0);

  let cumulative = 0;

  return (
    <>
      <div className="stack-bar">
        {safeTotal === 0 ? (
          <div className="stack-bar__empty">{t('charts.no_data')}</div>
        ) : (
          filled.map((p, i) => {
            const leftPct = (cumulative / safeTotal) * 100;
            cumulative += n(p.n);
            const rightPct = Math.max(0, (1 - cumulative / safeTotal) * 100);
            const pct = (n(p.n) / safeTotal) * 100;
            const bg = colorFor(p.label, i);
            const needsTip = pct < 18;
            const text =
              pct >= 18
                ? `${p.n.toLocaleString()} ${p.label}`
                : pct > 0
                  ? p.n.toLocaleString()
                  : '';

            return (
              <div
                key={p.label}
                className="stack-bar__segment"
                style={{
                  left: `${leftPct}%`,
                  right: `${rightPct}%`,
                  background: bg,
                  color: textOn(bg),
                  borderRight:
                    i === filled.length - 1 ? 'none' : '1px solid #fff',
                }}
                onMouseEnter={
                  needsTip
                    ? (e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTip({
                          label: p.label,
                          n: p.n,
                          x: rect.left + rect.width / 2,
                          y: rect.top,
                        });
                      }
                    : undefined
                }
                onMouseLeave={needsTip ? () => setTip(null) : undefined}
              >
                {text}
              </div>
            );
          })
        )}
      </div>

      {/* Tooltip portalled to document.body so it escapes any overflow/transform ancestor */}
      {tip &&
        createPortal(
          <div
            className="stack-bar-tooltip"
            style={{
              left: tip.x,
              top: tip.y,
              transform: 'translateX(-50%) translateY(calc(-100% - 6px))',
            }}
          >
            {tip.n.toLocaleString()} {tip.label}
          </div>,
          document.body
        )}
    </>
  );
}
